import React, { type FC, type SVGProps } from 'react'
import { useTranslation } from 'react-i18next'
import NaverCafeIcon from 'src/assets/icon/naver-cafe.svg?react'
import { useDynamicConstants } from 'src/services/constants'
import { cx } from 'src/utils/cx'
import IcBaselineDiscord from '~icons/ic/baseline-discord'
import MdiInstagram from '~icons/mdi/instagram'
import MdiYoutube from '~icons/mdi/youtube'
import RiTiktokFill from '~icons/ri/tiktok-fill'
import RiTwitterXFill from '~icons/ri/twitter-x-line'

export function useSocialMedia(options?: {
  locale?: string
  fixedItem?: Array<string>
}): Array<{
  key: string
  name: string
  icon: FC<Omit<SVGProps<SVGSVGElement>, 'xmlns' | 'viewBox'>>
  link: string | undefined
}> {
  const { data } = useDynamicConstants()
  const { i18n } = useTranslation()
  const locale = options?.locale || i18n.language

  const getLink = (
    link: string | Record<string, string> | undefined,
  ): string | undefined => {
    if (!link) return undefined
    if (typeof link === 'string') return link
    return link[locale] || link.en
  }

  const discord = {
    key: 'discord',
    name: 'Discord',
    icon: IcBaselineDiscord,
    link: getLink(data?.externalUrls?.discordJoin),
  }

  const x = {
    key: 'twitter',
    name: 'X',
    // Make this X icon smaller
    icon: ({ className }: { className?: string }) =>
      React.createElement(RiTwitterXFill, {
        className: cx(className, 'scale-90'),
      }),
    link: getLink(
      data?.externalUrls?.twitterAccounts ?? data?.externalUrls?.twitter,
    ),
  }

  const instagram = {
    key: 'instagram',
    name: 'Instagram',
    icon: MdiInstagram,
    link: getLink(data?.externalUrls?.instagram),
  }

  const tiktok = {
    key: 'tiktok',
    name: 'TikTok',
    icon: RiTiktokFill,
    link: getLink(data?.externalUrls?.tiktok),
  }

  const youtube = {
    key: 'youtube',
    name: 'YouTube',
    icon: MdiYoutube,
    link: getLink(data?.externalUrls?.youtubeAccounts),
  }

  const naverCafe = {
    key: 'naverCafe',
    name: 'Naver Cafe',
    icon: NaverCafeIcon,
    link: getLink(data?.externalUrls?.naverCafe),
  }

  const itemByKey = {
    [discord.key]: discord,
    [x.key]: x,
    [instagram.key]: instagram,
    [youtube.key]: youtube,
    [naverCafe.key]: naverCafe,
    [tiktok.key]: tiktok,
  }

  // Language-specific social media configurations, en is the fallback
  const languageConfigs = [
    { en: 'discord' },
    { en: 'twitter' },
    { en: 'instagram', ko: 'naverCafe' },
    { en: 'youtube' },
    { en: 'tiktok' },
  ]

  const socialMediasString: string[] = languageConfigs.flatMap(slotItem => {
    const availablePlatforms = Object.values(slotItem)
    const fixedPlatform = options?.fixedItem?.find(item =>
      availablePlatforms.includes(item),
    )
    const result =
      fixedPlatform ?? slotItem[locale as keyof typeof slotItem] ?? slotItem.en
    return result ? [result] : []
  })

  return socialMediasString.map(key => itemByKey[key])
}
